// AS TRAINING — Supplements Guide (evidence-based)

export const GOALS = [
  { id: "muscle_gain", label: "Ganancia muscular", short: "Músculo" },
  { id: "fat_loss", label: "Pérdida de grasa", short: "Grasa" },
  { id: "performance", label: "Rendimiento", short: "Rendimiento" },
  { id: "health", label: "Salud general", short: "Salud" },
  { id: "recovery", label: "Recuperación", short: "Recuperación" },
];

export const TIMING_SLOTS = [
  { id: "wake", label: "Al despertar" },
  { id: "pre_workout", label: "Antes de entrenar" },
  { id: "intra_workout", label: "Durante el entrenamiento" },
  { id: "post_workout", label: "Después de entrenar" },
  { id: "bedtime", label: "Antes de dormir" },
];

export const INTRO = {
  title: "GUÍA DE SUPLEMENTACIÓN AS TRAINING",
  what_is:
    "Un suplemento es un producto diseñado para complementar la dieta, no para sustituirla. Aporta nutrientes específicos (proteína, vitaminas, minerales, aminoácidos) que ayudan a cubrir necesidades cuando la alimentación no las alcanza.",
  myths: [
    { m: "Los suplementos sustituyen a la comida.", r: "No. Son un complemento. La base son los alimentos reales." },
    { m: "Sin suplementos no ganas masa muscular.", r: "Puedes progresar solo con dieta y entrenamiento. Los suplementos aceleran u optimizan procesos, no los sustituyen." },
    { m: "Cuanto más suplementos, mejor.", r: "Falso. Más no es mejor. Prioriza calidad, evidencia y dosis correctas." },
    { m: "Todos los suplementos tienen efecto inmediato.", r: "La mayoría requiere consistencia de semanas o meses para notar resultados." },
  ],
  priority: [
    "1. Alimentación equilibrada y suficiente en calorías y proteínas.",
    "2. Sueño y descanso adecuados (7-9 horas).",
    "3. Entrenamiento progresivo y constante.",
    "4. Hidratación adecuada.",
    "5. Solo entonces: suplementación estratégica según objetivo.",
  ],
};

export const SUPPLEMENTS = [
  {
    id: "whey",
    name: "Proteína Whey",
    category: "Proteína",
    evidence: "Alta",
    goals: ["muscle_gain", "recovery", "fat_loss"],
    timing: ["post_workout", "wake"],
    what: "Proteína de suero de leche de rápida absorción, rica en aminoácidos esenciales, especialmente leucina.",
    how_works:
      "Aporta un pico rápido de aminoácidos que estimula la síntesis de proteína muscular tras el entrenamiento.",
    benefits: [
      "Facilita alcanzar la cuota diaria de proteína.",
      "Estimula la síntesis muscular tras entrenar.",
      "Alto valor biológico y digestión rápida.",
      "Práctica y económica por porción.",
    ],
    when: "Post-entreno, entre comidas, al despertar o cuando no puedas hacer una comida sólida rica en proteína.",
    dosage: "20 - 40 g por toma. 1 - 2 tomas al día según necesidad diaria de proteína (1.6 - 2.2 g/kg/día).",
    side_effects: [
      "Molestias digestivas si eres intolerante a la lactosa (usa Whey Isolate o Vegana).",
      "Exceso puede causar gases o hinchazón.",
    ],
    recommended_for: [
      "Personas con requerimientos altos de proteína.",
      "Quienes tienen dificultad para llegar a su cuota diaria.",
      "Deportistas y practicantes de gimnasio.",
    ],
    avoid_if: [
      "Alergia severa a la proteína de leche.",
      "Intolerancia grave a la lactosa (opta por Isolate o vegana).",
    ],
  },
  {
    id: "creatina",
    name: "Creatina Monohidratada",
    category: "Rendimiento",
    evidence: "Alta",
    goals: ["muscle_gain", "performance", "recovery"],
    timing: ["post_workout", "wake"],
    what: "Compuesto natural presente en los músculos que ayuda a regenerar ATP, la energía inmediata durante esfuerzos cortos e intensos.",
    how_works:
      "Aumenta las reservas musculares de fosfocreatina, permitiendo levantar más peso o hacer más repeticiones en trabajos explosivos.",
    benefits: [
      "Aumenta fuerza y potencia.",
      "Favorece el crecimiento muscular a largo plazo.",
      "Mejora la recuperación entre series.",
      "Beneficios cognitivos (memoria y foco) según nuevas evidencias.",
    ],
    when: "Cualquier momento del día. La consistencia diaria importa más que el timing exacto.",
    dosage: "3 - 5 g al día. No requiere fase de carga. Todos los días, incluso en días de descanso.",
    side_effects: [
      "Ligera retención de agua intramuscular (no grasa).",
      "Molestias digestivas si se toma en dosis altas de golpe.",
    ],
    recommended_for: [
      "Cualquier persona que entrene fuerza o potencia.",
      "Deportes de alta intensidad.",
      "Adultos mayores para preservar masa muscular.",
    ],
    avoid_if: [
      "Enfermedad renal previa (consulta médica).",
      "Embarazo o lactancia (por precaución, no por daño demostrado).",
    ],
  },
  {
    id: "cafeina",
    name: "Cafeína",
    category: "Rendimiento",
    evidence: "Alta",
    goals: ["performance", "fat_loss"],
    timing: ["pre_workout", "wake"],
    what: "Estimulante del sistema nervioso central que aumenta el estado de alerta, la fuerza y la resistencia.",
    how_works:
      "Bloquea receptores de adenosina, reduciendo la sensación de fatiga y aumentando la liberación de adrenalina.",
    benefits: [
      "Más energía y concentración.",
      "Mayor rendimiento en fuerza y resistencia.",
      "Ligero aumento del gasto calórico.",
      "Retrasa la fatiga.",
    ],
    when: "30 - 45 minutos antes de entrenar. Evita después de las 4-5 PM si te afecta el sueño.",
    dosage: "3 - 6 mg por kg de peso corporal. Empieza por la dosis baja para evaluar tolerancia.",
    side_effects: [
      "Ansiedad, palpitaciones o insomnio en dosis altas.",
      "Dolor de estómago en ayunas.",
      "Tolerancia si se consume a diario en dosis altas.",
    ],
    recommended_for: [
      "Deportistas antes de sesiones intensas.",
      "Personas que necesitan energía pre-entreno.",
    ],
    avoid_if: [
      "Hipertensión no controlada.",
      "Arritmias o problemas cardíacos.",
      "Ansiedad severa o trastornos del sueño.",
      "Embarazo (limitar a menos de 200 mg/día).",
    ],
  },
  {
    id: "omega3",
    name: "Omega-3 (EPA/DHA)",
    category: "Salud",
    evidence: "Alta",
    goals: ["health", "recovery"],
    timing: ["wake", "post_workout"],
    what: "Ácidos grasos esenciales presentes en pescados grasos, con potente efecto antiinflamatorio.",
    how_works:
      "EPA y DHA regulan procesos inflamatorios, mejoran la salud cardiovascular y la función cerebral.",
    benefits: [
      "Reduce inflamación y dolor muscular tardío.",
      "Salud cardiovascular y cerebral.",
      "Mejora sensibilidad a la insulina.",
      "Apoyo en recuperación deportiva.",
    ],
    when: "Con las comidas principales (mejor absorción con grasas).",
    dosage: "2 - 3 g de EPA+DHA combinados al día.",
    side_effects: [
      "Regusto a pescado, reflujo.",
      "En dosis muy altas puede aumentar el tiempo de sangrado.",
    ],
    recommended_for: [
      "Personas que comen poco pescado azul.",
      "Deportistas con cargas altas de entrenamiento.",
      "Adultos preocupados por salud cardiovascular.",
    ],
    avoid_if: [
      "Uso de anticoagulantes (consulta médica).",
      "Cirugía próxima (suspender días antes).",
    ],
  },
  {
    id: "vitd",
    name: "Vitamina D3",
    category: "Salud",
    evidence: "Alta",
    goals: ["health", "recovery", "muscle_gain"],
    timing: ["wake"],
    what: "Vitamina hormonal producida por la piel con exposición solar. Deficiencia muy común en poblaciones modernas.",
    how_works:
      "Regula absorción de calcio, función inmune, muscular y hormonal (testosterona incluida).",
    benefits: [
      "Salud ósea y prevención de fracturas.",
      "Mejora función inmune.",
      "Ayuda a mantener niveles hormonales.",
      "Impacto en fuerza y recuperación.",
    ],
    when: "Con una comida que contenga grasa (mejora absorción).",
    dosage: "1000 - 4000 UI al día. Ideal medir niveles en sangre para ajustar.",
    side_effects: [
      "Toxicidad solo con dosis muy altas y sostenidas.",
      "Hipercalcemia si se abusa (>10.000 UI/día crónico).",
    ],
    recommended_for: [
      "Personas con poca exposición solar.",
      "Adultos mayores.",
      "Deportistas con déficit demostrado.",
    ],
    avoid_if: [
      "Hipercalcemia previa.",
      "Enfermedades granulomatosas (consulta médica).",
    ],
  },
  {
    id: "magnesio",
    name: "Magnesio",
    category: "Salud",
    evidence: "Alta",
    goals: ["recovery", "health"],
    timing: ["bedtime"],
    what: "Mineral esencial involucrado en más de 300 reacciones metabólicas. Deficiencia común.",
    how_works:
      "Participa en la contracción muscular, función nerviosa, síntesis proteica y calidad del sueño.",
    benefits: [
      "Mejora calidad del sueño.",
      "Reduce calambres musculares.",
      "Apoya recuperación tras entrenar.",
      "Regula estrés y ansiedad.",
    ],
    when: "Antes de dormir (formas glicinato o bisglicinato son las mejores).",
    dosage: "200 - 400 mg de magnesio elemental por día.",
    side_effects: [
      "Diarrea si se abusa (especialmente citrato u óxido).",
      "Somnolencia diurna con dosis altas.",
    ],
    recommended_for: [
      "Personas con estrés alto o mal sueño.",
      "Deportistas con calambres frecuentes.",
      "Dieta baja en verduras verdes y frutos secos.",
    ],
    avoid_if: [
      "Insuficiencia renal (consulta médica).",
      "Uso de ciertos antibióticos o diuréticos.",
    ],
  },
  {
    id: "electrolitos",
    name: "Electrolitos",
    category: "Hidratación",
    evidence: "Alta",
    goals: ["performance", "recovery"],
    timing: ["intra_workout", "pre_workout"],
    what: "Sales minerales (sodio, potasio, magnesio, cloro) que regulan hidratación, contracción muscular y balance hídrico.",
    how_works:
      "Reponen las pérdidas de sudor. Sin electrolitos, aunque bebas agua, la hidratación es incompleta.",
    benefits: [
      "Previene calambres y fatiga.",
      "Mejora rendimiento en sesiones largas o calor.",
      "Optimiza recuperación por hidratación completa.",
    ],
    when: "Antes o durante entrenamientos largos, en climas calurosos o si sudas mucho.",
    dosage:
      "Sigue las instrucciones del producto. Aproximadamente 500 - 1000 mg de sodio por sesión intensa.",
    side_effects: [
      "Exceso de sodio en personas hipertensas.",
      "Molestias digestivas si es muy concentrado.",
    ],
    recommended_for: [
      "Deportistas de resistencia.",
      "Entrenamientos largos (>60 min) o en calor.",
      "Personas que sudan mucho.",
    ],
    avoid_if: [
      "Hipertensión no controlada (limita el sodio).",
      "Enfermedad renal.",
    ],
  },
  {
    id: "caseina",
    name: "Caseína",
    category: "Proteína",
    evidence: "Alta",
    goals: ["muscle_gain", "recovery"],
    timing: ["bedtime"],
    what: "Proteína de leche de digestión lenta que libera aminoácidos durante 6-8 horas.",
    how_works:
      "Mantiene un aporte constante de aminoácidos durante el sueño, cuando pasas más horas sin comer.",
    benefits: [
      "Aporte prolongado de aminoácidos.",
      "Ideal antes de dormir para recuperación nocturna.",
      "Mayor saciedad que Whey.",
    ],
    when: "Antes de dormir o entre comidas largas.",
    dosage: "20 - 40 g antes de acostarte.",
    side_effects: [
      "Digestión lenta puede causar hinchazón.",
      "No recomendada con intolerancia a la lactosa severa.",
    ],
    recommended_for: [
      "Personas que buscan optimizar la recuperación nocturna.",
      "Quienes tienen períodos largos sin comer.",
    ],
    avoid_if: [
      "Alergia a proteína de leche.",
      "Intolerancia severa a la lactosa.",
    ],
  },
  {
    id: "proteina_vegetal",
    name: "Proteína Vegetal",
    category: "Proteína",
    evidence: "Alta",
    goals: ["muscle_gain", "recovery", "health"],
    timing: ["post_workout", "wake"],
    what: "Proteína en polvo elaborada con guisante, arroz, cáñamo o soja. Alternativa vegana al whey.",
    how_works:
      "Aporta aminoácidos esenciales. Las mezclas (guisante + arroz) igualan el perfil del whey.",
    benefits: [
      "Apta para veganos y vegetarianos.",
      "Sin lactosa, más fácil de digerir.",
      "Sustentable y con buena composición.",
    ],
    when: "Post-entreno, entre comidas o al despertar.",
    dosage: "25 - 40 g por toma.",
    side_effects: [
      "Molestias digestivas en algunas personas.",
      "Sabor menos aceptado que el whey.",
    ],
    recommended_for: [
      "Veganos, vegetarianos.",
      "Intolerantes a la lactosa.",
      "Quienes prefieren proteína no animal.",
    ],
    avoid_if: [
      "Alergia a soja o legumbres específicas del producto.",
    ],
  },
  {
    id: "multivitaminico",
    name: "Multivitamínicos",
    category: "Salud",
    evidence: "Media",
    goals: ["health"],
    timing: ["wake"],
    what: "Combinación de vitaminas y minerales en dosis moderadas para cubrir necesidades básicas.",
    how_works:
      "Actúan como red de seguridad nutricional cuando la dieta no cubre todos los micronutrientes.",
    benefits: [
      "Cubre deficiencias leves.",
      "Apoyo general al sistema inmune.",
      "Cómodo para viajes o dietas restrictivas.",
    ],
    when: "Con una comida principal.",
    dosage: "Una porción diaria según indicaciones del producto.",
    side_effects: [
      "Náuseas si se toma en ayunas.",
      "Interacciones con medicamentos específicos.",
    ],
    recommended_for: [
      "Dietas restrictivas o hipocalóricas.",
      "Períodos de estrés alto o viajes.",
      "Personas con alimentación irregular.",
    ],
    avoid_if: [
      "Ya se toman megadosis de vitaminas por separado.",
      "Enfermedades específicas que requieren restricciones.",
    ],
  },
  {
    id: "colageno",
    name: "Colágeno Hidrolizado",
    category: "Recuperación",
    evidence: "Media",
    goals: ["recovery", "health"],
    timing: ["wake", "pre_workout"],
    what: "Proteína estructural (piel, tendones, cartílagos). En polvo hidrolizado se absorbe como péptidos.",
    how_works:
      "Aporta aminoácidos específicos (glicina, prolina, hidroxiprolina) que pueden ayudar en la síntesis de tejido conectivo. Evidencia moderada, no concluyente.",
    benefits: [
      "Posible apoyo a articulaciones y tendones.",
      "Puede mejorar salud de piel y uñas.",
      "Fácil de mezclar en café o batidos.",
    ],
    when: "30 - 60 min antes de ejercicio articular (con vitamina C mejora absorción).",
    dosage: "10 - 20 g al día.",
    side_effects: [
      "Molestias digestivas leves.",
      "Sabor si es sin sabor añadido.",
    ],
    recommended_for: [
      "Personas con molestias articulares recurrentes.",
      "Adultos mayores o deportistas de alto impacto.",
    ],
    avoid_if: [
      "Alergia a la fuente (bovino, porcino o marino).",
    ],
    evidence_note: "La evidencia es moderada. No sustituye a un tratamiento médico para problemas articulares.",
  },
  {
    id: "citrulina",
    name: "Citrulina Malato",
    category: "Rendimiento",
    evidence: "Media-Alta",
    goals: ["performance", "muscle_gain"],
    timing: ["pre_workout"],
    what: "Aminoácido que aumenta los niveles de arginina y óxido nítrico en sangre.",
    how_works:
      "Mejora el flujo sanguíneo, aporte de nutrientes y eliminación de amoniaco muscular.",
    benefits: [
      "Reduce la fatiga durante entrenamientos largos.",
      "Aumenta la bomba muscular.",
      "Mayor volumen y repeticiones por serie.",
    ],
    when: "30 - 45 min antes de entrenar.",
    dosage: "6 - 8 g de citrulina malato (o 3 - 5 g de L-citrulina pura) pre-entreno.",
    side_effects: [
      "Molestias digestivas en dosis muy altas.",
    ],
    recommended_for: [
      "Practicantes de gimnasio que buscan más volumen.",
      "Deportes de resistencia y alta intensidad.",
    ],
    avoid_if: [
      "Uso de medicamentos para presión arterial (consulta médica).",
    ],
  },
  {
    id: "beta_alanina",
    name: "Beta-Alanina",
    category: "Rendimiento",
    evidence: "Alta",
    goals: ["performance", "muscle_gain"],
    timing: ["pre_workout"],
    what: "Aminoácido que aumenta las reservas musculares de carnosina, amortiguando la acidez muscular.",
    how_works:
      "Al retrasar la acidificación, permite mantener alta intensidad durante más tiempo (esfuerzos de 1 a 4 minutos).",
    benefits: [
      "Más repeticiones en series largas.",
      "Retrasa la fatiga en esfuerzos submáximos.",
      "Complementa a la creatina.",
    ],
    when: "Dividida en 2 - 3 tomas al día. La consistencia diaria importa más que el timing.",
    dosage: "3 - 5 g al día repartidos.",
    side_effects: [
      "Parestesia (hormigueo en la piel) — inofensivo y temporal.",
    ],
    recommended_for: [
      "Deportes de alta intensidad (CrossFit, HIIT, gimnasio, remo).",
      "Series de 8 - 20 reps o esfuerzos de 1 - 4 min.",
    ],
    avoid_if: [
      "Molestia con la parestesia (usa versión de liberación sostenida).",
    ],
  },
];

export const COMBINATIONS = [
  {
    title: "Creatina + Proteína Whey",
    verdict: "Excelente",
    why: "Sinergia clara. La creatina mejora el rendimiento y la proteína aporta el sustrato para reparar y crecer.",
    ok: true,
  },
  {
    title: "Creatina + Beta-Alanina",
    verdict: "Excelente",
    why: "Trabajan en sistemas energéticos diferentes: fosfocreatina (creatina) + tampón de acidez (beta-alanina).",
    ok: true,
  },
  {
    title: "Cafeína + Citrulina Malato",
    verdict: "Muy buena",
    why: "Cafeína aporta estimulación central, citrulina mejora flujo sanguíneo y bomba. Combinación clásica pre-entreno.",
    ok: true,
  },
  {
    title: "Whey + Caseína (post-entreno)",
    verdict: "Redundante",
    why: "Ambas son proteína. Post-entreno prioriza whey (rápida). Guarda caseína para antes de dormir.",
    ok: false,
  },
  {
    title: "Multivitamínico + Vitamina D por separado",
    verdict: "Posible sobredosis",
    why: "Revisa las UI de vitamina D del multivitamínico antes de sumar más. Puede ser innecesario o excesivo.",
    ok: false,
  },
  {
    title: "Omega-3 + Vitamina D",
    verdict: "Excelente",
    why: "Ambos con las comidas (mejor absorción con grasas). Sinergia en salud cardiovascular e inmune.",
    ok: true,
  },
  {
    title: "BCAA + Proteína Whey",
    verdict: "Redundante",
    why: "El whey ya contiene BCAAs suficientes. Añadir BCAAs no aporta beneficio si ya tomas proteína completa.",
    ok: false,
  },
  {
    title: "Colágeno + Vitamina C",
    verdict: "Recomendada",
    why: "La vitamina C favorece la síntesis de colágeno. Tomarlos juntos mejora la absorción y aprovechamiento.",
    ok: true,
  },
];

export const FAQ = [
  {
    q: "¿Necesito tomar suplementos para ganar masa muscular?",
    a: "No. Puedes ganar masa muscular solo con dieta suficiente en proteína (1.6 - 2.2 g/kg) y entrenamiento progresivo. Los suplementos aceleran u optimizan procesos.",
  },
  {
    q: "¿Los suplementos son seguros?",
    a: "Los con evidencia científica (Whey, Creatina, Cafeína, Omega-3, Vitamina D) son seguros en dosis recomendadas para personas sanas. Consulta con un profesional si tienes condiciones médicas.",
  },
  {
    q: "¿Cuánto tiempo tarda en verse el efecto de un suplemento?",
    a: "Depende. La cafeína actúa en 30-45 min. La creatina requiere 2-4 semanas de uso continuo. Vitamina D y Omega-3, meses. La consistencia importa más que el timing.",
  },
  {
    q: "¿Puedo tomar creatina en ciclos o de forma continua?",
    a: "Continua. No se requieren ciclos. La evidencia muestra seguridad en uso continuo en personas sanas.",
  },
  {
    q: "¿La proteína en polvo daña el riñón o hígado?",
    a: "No en personas sanas. Los estudios no muestran daño renal o hepático por dietas altas en proteína en poblaciones saludables. Si tienes enfermedad renal previa, consulta médica.",
  },
  {
    q: "¿Debo tomar suplementos en días de descanso?",
    a: "Sí para creatina, vitaminas, omega-3 y colágeno (dependen de consistencia). Los pre-entrenos (cafeína, citrulina) solo antes de entrenar.",
  },
  {
    q: "¿Puedo tomar cafeína y pre-entreno juntos?",
    a: "Solo si sumas cuidadosamente la dosis total de cafeína. No pases de 400 mg/día. Muchos pre-entrenos ya llevan cafeína.",
  },
  {
    q: "¿Los BCAAs son necesarios si ya tomo proteína?",
    a: "No. La proteína completa (whey, caseína, proteína vegetal mixta) ya aporta los BCAAs necesarios. Los BCAAs por separado son innecesarios.",
  },
  {
    q: "¿Cuál es el mejor suplemento para bajar de grasa?",
    a: "Ninguno es mágico. La cafeína puede aumentar ligeramente el gasto calórico. La proteína ayuda a mantener músculo en déficit. La base sigue siendo el déficit calórico y el entrenamiento.",
  },
  {
    q: "¿Puedo tomar suplementos si tengo más de 50 años?",
    a: "Sí. Creatina, Whey, Vitamina D y Omega-3 son especialmente recomendados en adultos mayores para preservar masa muscular, huesos y salud cardiovascular.",
  },
];

export const SAFETY = [
  "Los suplementos NO sustituyen una alimentación equilibrada.",
  "En embarazo, lactancia, enfermedades crónicas o uso de medicamentos, SIEMPRE consulta con un profesional de la salud antes de suplementar.",
  "Respeta las dosis recomendadas. Más no significa mejor.",
  "Compra en marcas con certificación (Informed-Sport, NSF, GMP).",
  "Si sientes síntomas anómalos (palpitaciones, dolor abdominal, alergias), suspende el uso y consulta.",
  "Esta guía es educativa y no reemplaza la valoración de un médico, nutricionista o profesional cualificado.",
];
